/**
 * @file LabelPrintSummary.tsx
 * @description Нижний блок диалога печати: «Начать с наклейки» по каждому
 *   шаблону печати, сводка по листам и подсказка про принтер (phase19.22,
 *   W17-5a/5b; ui-design §1, §2).
 *
 * «Начать с» — отдельно для каждого шаблона: у стандарта и мини разные
 * листы, и начат может быть только один из них. Показываем поле только тем
 * шаблонам, что реально идут в печать.
 *
 * Принимает не всю форму, а только поля сводки — ими же отвечает форма
 * печати этикеток ячеек (phase19.22 шаг 5). Включены «Линии реза» — строка
 * «с линиями реза» под сводкой: видно до печати, что PDF для обычной бумаги.
 */

import { Info, Scissors, TriangleAlert } from "lucide-react";

import { LabelSheetStartPicker } from "./LabelSheetStartPicker";
import { LABEL_TEXT, manySheetsWarning } from "./label-texts";
import type { LabelPrintForm } from "./useLabelPrintForm";
import { SectionLabel } from "../../ui/SectionLabel";
import {
  LABEL_MANY_THRESHOLD,
  LABEL_MAX_TOTAL,
  formatSheetsSummary,
} from "../../../lib/label-sheets";

interface Props {
  form: Pick<LabelPrintForm, "summary" | "startAt" | "setStartAt" | "overLimit" | "cutLines"> & {
    /** Потолок этикеток за печать: у адресов он свой (500). */
    maxTotal?: number;
  };
}

export function LabelPrintSummary({ form }: Props) {
  const { summary } = form;
  if (summary.groups.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <SectionLabel as="div">Начать с наклейки</SectionLabel>
      {summary.groups.map((group) => (
        <LabelSheetStartPicker
          key={group.template}
          template={group.template}
          value={form.startAt[group.template] ?? 1}
          onChange={(value) => form.setStartAt(group.template, value)}
        />
      ))}

      <p className="text-sm font-medium tabular-nums">{formatSheetsSummary(summary)}</p>
      {form.cutLines ? (
        <p className="-mt-2 flex items-center gap-1.5 text-sm text-on-surface-variant">
          <Scissors className="size-3.5 shrink-0" aria-hidden />
          {LABEL_TEXT.cutLinesSummary}
        </p>
      ) : null}

      {form.overLimit ? (
        <p className="flex items-start gap-1.5 text-xs text-destructive">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          Не больше {form.maxTotal ?? LABEL_MAX_TOTAL} этикеток за одну печать — уменьшите количество.
        </p>
      ) : summary.totalLabels > LABEL_MANY_THRESHOLD ? (
        <p className="flex items-start gap-1.5 text-xs text-warning">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {manySheetsWarning(summary.totalSheets)}
        </p>
      ) : null}

      <p className="flex items-start gap-1.5 text-xs text-on-surface-variant">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        {LABEL_TEXT.printerHint}
      </p>
    </div>
  );
}
